/**
 * «رخدادهای تو» — نوارِ پینِ بالای صفحه‌ی کشف. قرینه‌ی
 * apps/mobile/src/features/discover/MyEventsStrip.tsx، با همان چهار قید:
 *
 *  • فقط خودِ فرستنده می‌بیندش (سیاستِ `submitted_by = auth.uid()`).
 *  • بالای فهرست است نه داخلش، پس جستجو و فیلتر و ترتیب از رویش رد نمی‌شوند.
 *  • تا **تاریخِ پایان** می‌ماند، نه تا شروع (`pinnedEvents` در core).
 *  • وضعیتش را می‌گوید: تایید شده یا منتظرِ تایید.
 *
 * ⚠ **سه چیز، نه بیشتر: عکس، عنوان، وضعیت.** این فهرست جای مرورِ سریع است،
 *   نه جای جزئیات. تاریخ و آمار و دکمه‌ی ویرایش قبلاً همین‌جا بودند و هر
 *   رخداد را به یک بلوکِ چهارخطی تبدیل می‌کردند؛ همه‌شان رفتند به صفحه‌ی
 *   خودِ رخداد، یک کلیک آن‌طرف‌تر (`ItemPage`، بخشِ صاحبِ رخداد).
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { pinnedEvents, type MyEvent } from '@/core/lib/myEvents';
import { SafeImg } from '@/ui/SafeImg';
import { Card } from '@/ui/kit';

/** کلاسِ نشانِ وضعیت — همان سه رنگی که کارت‌های دیگر هم دارند. */
const TONE: Record<MyEvent['state'], string> = {
  pending: 'count wait',
  live: 'count ok',
  editPending: 'count wait',
  rejected: 'count danger',
};

/**
 * یک **کلمه**، نه یک جمله.
 *
 * سوالی که این ردیف جواب می‌دهد همین است: قبول شده یا نه. جمله‌ی کاملش روی
 * صفحه‌ی خودِ رخداد است — برای همین `editPending` هم همان «تایید شد» را
 * می‌گیرد: خودِ رخداد تایید شده و آن ویرایشِ معلق یک جزئیات است.
 */
const LABEL: Record<MyEvent['state'], string> = {
  pending: 'myEventShortWaiting',
  live: 'myEventShortApproved',
  editPending: 'myEventShortApproved',
  rejected: 'myEventShortRejected',
};

/**
 * رخدادهای پین‌شده‌ی خودم، و شناسه‌هایشان.
 *
 * دو مصرف دارد و برای همین هوک است: خودِ این نوار، و **فهرستِ کشف** که باید
 * همین‌ها را از ردیف‌هایش بیرون بگذارد. رخدادی که بالا پین شده اگر پایین هم
 * بیاید، کاربر یک چیز را دو بار می‌بیند — همان چیزی که گزارش شد.
 *
 * دو بار صدا زدنش هزینه‌ای ندارد: کلیدِ react-query یکی است، پس یک درخواست.
 */
export function useMyPins(cityId: number | null) {
  const { profile } = useSession();

  const mine = useQuery({
    queryKey: ['myEvents', cityId],
    queryFn: () => eventRepo.mine(profile!.id, cityId!),
    enabled: !!profile && cityId !== null,
  });

  const pins = useMemo(() => pinnedEvents(mine.data ?? []), [mine.data]);
  /** شناسه‌ی همان ردیفی که در فهرستِ عمومی هم هست (نسخه‌ی منتشرشده). */
  const ids = useMemo(() => new Set(pins.map((p) => p.item.id)), [pins]);

  return { pins, ids };
}

export function MyEventsStrip({ cityId }: { cityId: number | null }) {
  const { profile } = useSession();
  const { pins } = useMyPins(cityId);

  if (!profile || pins.length === 0) return null;

  return (
    <section className="my-events">
      <h2 className="section-title">{t('myEvents')}</h2>
      {pins.map((pin) => (
        <Link key={pin.rootId} to={`/event/${pin.item.id}`} className="pin-link">
          <Card className="pin-card">
            {pin.item.imageUrl
              ? <SafeImg className="pin-img" src={pin.item.imageUrl} loading="lazy"
                  screen="web.myEvents" />
              : <span className="pin-img pin-img-empty" aria-hidden="true" />}
            {/* وضعیت انتهای همان سطر می‌نشیند، نه زیرِ عنوان. */}
            <span className="pin-title">{pin.item.title}</span>
            <span className={TONE[pin.state]}>{t(LABEL[pin.state])}</span>
          </Card>
        </Link>
      ))}
    </section>
  );
}
